import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { searchApi } from "@/api/endpoints";
import type { GlobalSearchResult } from "@/api/types";

export function GlobalSearch() {
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<GlobalSearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [focusIndex, setFocusIndex] = useState(-1);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setOpen(false);
      setFocusIndex(-1);
      setSearchError(null);
      return;
    }
    const handle = window.setTimeout(() => {
      setLoading(true);
      setSearchError(null);
      searchApi
        .global(query.trim())
        .then(({ results: items, errors }) => {
          setResults(items);
          setOpen(true);
          setFocusIndex(items.length > 0 ? 0 : -1);
          if (errors.length) {
            setSearchError(errors.join(" · "));
          }
        })
        .catch((err) => {
          setResults([]);
          setOpen(true);
          setFocusIndex(-1);
          setSearchError(err instanceof Error ? err.message : "Search failed");
        })
        .finally(() => setLoading(false));
    }, 300);
    return () => window.clearTimeout(handle);
  }, [query]);

  useEffect(() => {
    function onPointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  function goTo(result: GlobalSearchResult) {
    navigate(result.path);
    setQuery("");
    setOpen(false);
    setResults([]);
    setFocusIndex(-1);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (!open || results.length === 0) {
      if (event.key === "Escape") setOpen(false);
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setFocusIndex((index) => (index + 1) % results.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setFocusIndex((index) => (index <= 0 ? results.length - 1 : index - 1));
    } else if (event.key === "Enter" && focusIndex >= 0) {
      event.preventDefault();
      goTo(results[focusIndex]);
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  }

  const grouped = {
    client: results.filter((item) => item.type === "client"),
    claim: results.filter((item) => item.type === "claim"),
    policy: results.filter((item) => item.type === "policy"),
  };

  let runningIndex = -1;

  return (
    <div className="global-search" ref={rootRef}>
      <input
        type="search"
        className="global-search-input"
        placeholder="Search clients, claims, policies…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => {
          if (query.trim()) setOpen(true);
        }}
        onKeyDown={onKeyDown}
        aria-label="Global search"
        aria-expanded={open}
      />
      {open ? (
        <div className="global-search-dropdown card">
          {searchError ? (
            <p className="global-search-error" style={{ padding: "0.75rem", margin: 0 }}>
              {searchError}
            </p>
          ) : null}
          {loading ? (
            <p className="empty-state" style={{ padding: "0.75rem" }}>
              Searching…
            </p>
          ) : results.length === 0 ? (
            <p className="empty-state" style={{ padding: "0.75rem" }}>
              No results for &ldquo;{query.trim()}&rdquo;
            </p>
          ) : (
            <>
              {(["client", "claim", "policy"] as const).map((section) => {
                const items = grouped[section];
                if (items.length === 0) return null;
                const label = section === "client" ? "Clients" : section === "claim" ? "Claims" : "Policies";
                return (
                  <div key={section} className="global-search-section">
                    <div className="global-search-section-label">{label}</div>
                    <ul>
                      {items.map((item) => {
                        runningIndex += 1;
                        const itemIndex = runningIndex;
                        return (
                          <li key={`${item.type}-${item.id}`}>
                            <button
                              type="button"
                              className={`global-search-result${focusIndex === itemIndex ? " focused" : ""}`}
                              onMouseEnter={() => setFocusIndex(itemIndex)}
                              onClick={() => goTo(item)}
                            >
                              <span>{item.label}</span>
                              <span className={`search-type-badge search-type-${item.type}`}>{item.type}</span>
                              {item.meta ? <span className="global-search-meta">{item.meta}</span> : null}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
